import React, {ComponentType, useCallback, useEffect, useMemo} from "react"
import {Field, FieldProps, Formik, FormikProps} from "formik"
import {useToasts} from "react-toast-notifications"
import {useAccessToken} from "../../services/auth"
import {OrgSettings, update, JWTProviders} from "../../resources/orgSettings"
import Auth0Fields from "../../components/JwtProviders/custom/Auth0Fields"
import StytchFileds from "../../components/JwtProviders/custom/StytchFileds"
import useAsyncResultIdle from "../../hooks/useAsyncResultIdle"
import CognitoFields from "../../components/JwtProviders/custom/CognitoFields"
import OktaFields from "../../components/JwtProviders/custom/OktaFields"
import Icon from "../../components/Icon/Icon"
import {JwtSettingsFormValues} from "./types"

const providerFieldsMap: Partial<Record<JWTProviders, ComponentType>> = {
	[JWTProviders.Auth0]: Auth0Fields,
	[JWTProviders.Stytch]: StytchFileds,
	[JWTProviders.Cognito]: CognitoFields,
	[JWTProviders.Okta]: OktaFields,
}

export default function JwtSettings({orgSettings, refresh}: {orgSettings: OrgSettings; refresh: () => void}) {
	const accessToken = useAccessToken()
	const [state, patch] = useAsyncResultIdle(update)
	const {addToast} = useToasts()

	useEffect(() => {
		if (state.isOk()) {
			addToast("Org Settings Updated Successfully", {appearance: "success"})
			refresh()
		}
	}, [state])

	const initialValues = useMemo<JwtSettingsFormValues>(() => {
		return {
			provider: orgSettings?.attributes?.jwtSettings?.provider ?? JWTProviders.Auth0,
			jwksPath: orgSettings?.attributes?.jwtSettings?.jwksPath ?? "",
			issuer: orgSettings?.attributes?.jwtSettings?.issuer ?? undefined,
		}
	}, [orgSettings])

	const handleSubmit = useCallback(
		(values: JwtSettingsFormValues) => {
			patch(accessToken, {
				jwtSettings: {
					provider: values.provider,
					jwksPath: values.jwksPath,
					issuer: values.issuer,
				},
			})
		},
		[patch, accessToken]
	)

	return (
		<div className={"card"}>
			<div className={"org-settings-content"}>
				<h3> JWT Settings </h3>
				<div className="jwt-description">
					If you are using one of Unit&apos;s supported authentication providers, you can rely on the authentication you
					already implemented to obtain a Unit customer token. In order to enable Custom Authentication choose your
					authentication provider from the list below and follow the{" "}
					<a href="https://guides.unit.co/custom-authentication/" target="_blank" rel="noreferrer">
						Custom Authentication guide
					</a>{" "}
					<Icon icon="maximize-square-4--interface-essential" size={12} />
				</div>
				<Formik initialValues={initialValues} onSubmit={handleSubmit}>
					{({handleSubmit, values: {provider}, resetForm}: FormikProps<JwtSettingsFormValues>) => {
						const Fields = providerFieldsMap[provider]

						const handleProviderChange = useCallback(
							(event: React.ChangeEvent<HTMLSelectElement>) => {
								const newProvider = event.target.value as JWTProviders
								resetForm({
									values: {
										provider: newProvider,
										jwksPath: "",
										issuer: "",
									},
								})
							},
							[resetForm]
						)

						return (
							<form onSubmit={handleSubmit}>
								<label className="label" htmlFor="jwt-provider">
									Provider
								</label>
								<div className="select">
									<Field name="provider">
										{({field: {value, name}}: FieldProps<JWTProviders>) => (
											<select id="jwt-provider" name={name} value={value} onChange={handleProviderChange}>
												<option value={JWTProviders.Auth0}>Auth0</option>
												<option value={JWTProviders.Stytch}>Stytch</option>
												<option value={JWTProviders.Cognito}>Amazon Cognito</option>
												<option value={JWTProviders.Okta}>Okta</option>
											</select>
										)}
									</Field>
								</div>

								{Fields && <Fields />}

								<div className="columns">
									<div className="column mt-4">
										{state.match(
											() => (
												<button className="button is-success">Update</button>
											),
											() => (
												<button className="button is-success is-loading">Updating</button>
											),
											(_) => null,
											(err) => (
												<>
													<div className="has-text-danger">{err.errors[0].title}</div>
													<button className="button is-success">Update</button>
												</>
											)
										)}
									</div>
								</div>
							</form>
						)
					}}
				</Formik>
			</div>
		</div>
	)
}
